import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  LayoutDashboard, Receipt, Server, Boxes, Users, BarChart3,
  RefreshCw, LogOut, Coins, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useRefresh } from "@/context/RefreshContext";
import { relTime } from "@/lib/format";

const ADMIN_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/transactions", label: "Transactions", icon: Receipt },
  { to: "/machines", label: "Machines", icon: Server },
  { to: "/groups", label: "Machine Groups", icon: Boxes },
  { to: "/merchants", label: "Merchants", icon: Users },
];
const MERCHANT_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/machines", label: "My Machines", icon: Server },
  { to: "/transactions", label: "My Transactions", icon: Receipt },
  { to: "/performance", label: "Performance", icon: BarChart3 },
];

export default function Shell() {
  const { user, logout } = useAuth();
  const { bump, lastRefreshed } = useRefresh();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const nav = user.role === "admin" ? ADMIN_NAV : MERCHANT_NAV;
  const [now, setNow] = useState(Date.now());
  useState(() => setInterval(() => setNow(Date.now()), 5000));

  const doLogout = () => { logout(); navigate("/login"); };

  const SideContent = (
    <>
      <div className="flex items-center gap-2 px-5 h-16 border-b border-border">
        <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-white text-black">
          <Coins className="h-5 w-5" />
        </div>
        <div>
          <div className="font-head font-black tracking-tight leading-none">PlayMint</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Ops Portal</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1">
        {nav.map((n) => (
          <NavLink
            key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
            data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-sm px-3 py-2 text-sm transition-colors ${
                isActive ? "bg-[#262626] text-white" : "text-muted-foreground hover:bg-[#1c1c1c] hover:text-white"
              }`
            }
          >
            <n.icon className="h-4 w-4" /> {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <div className="px-2 pb-2">
          <div className="text-sm font-medium truncate">{user.name}</div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{user.role}</div>
        </div>
        <button
          data-testid="logout-btn" onClick={doLogout}
          className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-sm text-muted-foreground hover:bg-[#1c1c1c] hover:text-white transition-colors"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* desktop sidebar */}
      <aside className="hidden md:flex w-60 flex-col border-r border-border bg-[#0d0d0d] fixed inset-y-0">
        {SideContent}
      </aside>

      {/* mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 flex flex-col bg-[#0d0d0d] border-r border-border">{SideContent}</div>
          <div className="flex-1 bg-black/60" onClick={() => setOpen(false)} />
        </div>
      )}

      <div className="flex-1 md:ml-60 flex flex-col min-w-0">
        <header className="sticky top-0 z-40 flex items-center gap-3 h-16 px-4 md:px-6 border-b border-border bg-[#0A0A0A]">
          <button className="md:hidden" onClick={() => setOpen(true)} data-testid="menu-btn">
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-[#10B981] pulse-dot" />
            <span className="mono hidden sm:inline" data-testid="last-refreshed">Updated {relTime(Math.floor(lastRefreshed / 1000))}</span>
          </div>
          <button
            data-testid="refresh-btn" onClick={bump}
            className="flex items-center gap-2 rounded-sm border border-border px-3 py-1.5 text-sm hover:bg-[#1c1c1c] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Refresh</span>
          </button>
        </header>
        <main className="flex-1 p-4 md:p-6 max-w-[1400px] w-full">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
